import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, AlertCircle, Bell, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { authenticatedFetch, getUserData } from "@/lib/auth";
import { TicketTimeline } from "@/components/TicketTimeline";
import { TicketInteractionForm, InteractionData } from "@/components/TicketInteractionForm";
import { cn } from "@/lib/utils";

interface Ticket {
  id: number;
  ticket_number: string;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  created_at: string;
  updated_at: string;
  client_id: number;
  company_name: string;
  assigned_to?: number;
  assigned_to_name?: string;
}

interface TimelineInteraction {
  id: number;
  ticket_id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  user_type: 'client' | 'internal';
  interaction_type: string;
  message: string;
  metadata?: any;
  is_internal: boolean;
  created_at: string;
  attachments?: any[];
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  novo: { label: "Novo", color: "bg-blue-500/20 text-blue-700 dark:text-blue-400" },
  em_atendimento: { label: "Em Atendimento", color: "bg-purple-500/20 text-purple-700 dark:text-purple-400" },
  aguardando_resposta: { label: "Aguardando Resposta", color: "bg-yellow-500/20 text-yellow-700 dark:text-yellow-400" },
  resolvido: { label: "Resolvido", color: "bg-green-500/20 text-green-700 dark:text-green-400" },
  fechado: { label: "Fechado", color: "bg-gray-500/20 text-gray-700 dark:text-gray-400" },
  cancelado: { label: "Cancelado", color: "bg-red-500/20 text-red-700 dark:text-red-400" },
};

const PRIORITY_CONFIG: Record<string, { label: string; color: string }> = {
  baixa: { label: "Baixa", color: "border-green-500 text-green-700 dark:text-green-400" },
  media: { label: "Média", color: "border-yellow-500 text-yellow-700 dark:text-yellow-400" },
  alta: { label: "Alta", color: "border-orange-500 text-orange-700 dark:text-orange-400" },
  urgente: { label: "Urgente", color: "border-red-500 text-red-700 dark:text-red-400" },
};

const ClientTicketDetailNew = () => {
  const { id } = useParams<{ id: string }>();
  const currentUser = getUserData();

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [timeline, setTimeline] = useState<TimelineInteraction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    if (id) {
      fetchTicketData();
    }
  }, [id]);

  const fetchTicketData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('[Ticket] Buscando ticket ID:', id);
      console.log('[Ticket] URL:', `/api/tickets/index.php?id=${id}`);

      // Buscar detalhes do ticket e timeline
      const ticketResponse = await authenticatedFetch(`/api/tickets/index.php?id=${id}`);
      
      console.log('[Ticket] Response status:', ticketResponse.status);
      console.log('[Ticket] Response headers:', Object.fromEntries(ticketResponse.headers.entries()));
      
      if (!ticketResponse.ok) {
        const errorText = await ticketResponse.text();
        console.error('[Ticket] Error response:', errorText);
        throw new Error("Erro ao carregar ticket");
      }

      const responseText = await ticketResponse.text();
      console.log('[Ticket] Response text (first 500 chars):', responseText.substring(0, 500));
      
      let ticketData;
      try {
        ticketData = JSON.parse(responseText);
      } catch (parseError) {
        console.error('[Ticket] JSON parse error:', parseError);
        console.error('[Ticket] Full response:', responseText);
        throw new Error("Resposta inválida do servidor");
      }
      
      console.log('[Ticket] Parsed data:', ticketData);
      
      if (ticketData.ticket) {
        setTicket(ticketData.ticket);
      }
      
      if (ticketData.timeline) {
        setTimeline(ticketData.timeline);
      }

    } catch (err) {
      console.error("Erro ao carregar ticket:", err);
      setError(err instanceof Error ? err.message : "Erro desconhecido");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInteraction = async (data: InteractionData) => {
    try {
      setIsSubmitting(true);
      setMessage(null);

      // Se houver anexos, usar FormData
      let body: any;
      
      if (data.attachments && data.attachments.length > 0) {
        const formData = new FormData();
        formData.append('type', data.type);
        formData.append('message', data.message);
        if (data.metadata) {
          formData.append('metadata', JSON.stringify(data.metadata));
        }
        
        // Adicionar arquivos
        data.attachments.forEach((file) => {
          formData.append('attachments[]', file);
        });
        
        body = formData;
      } else {
        // Sem anexos, usar JSON
        body = JSON.stringify(data);
      }

      const response = await authenticatedFetch(`/api/tickets/${id}/interact`, {
        method: 'POST',
        body
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao enviar interação');
      }

      const result = await response.json();

      if (result.success) {
        setMessage({ type: "success", text: "Interação enviada com sucesso!" });
        
        // Atualizar ticket e timeline
        if (result.data.ticket) {
          setTicket(result.data.ticket);
        }
        if (result.data.timeline) {
          setTimeline(result.data.timeline);
        }
      }

    } catch (err) {
      console.error("Erro ao enviar interação:", err);
      setMessage({ 
        type: "error", 
        text: err instanceof Error ? err.message : "Erro ao enviar interação" 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleValidation = async (resolved: boolean) => {
    const confirmMessage = resolved 
      ? "Confirmar que o problema foi resolvido?"
      : "Informar que ainda há problemas?";
    
    if (!confirm(confirmMessage)) return;

    const message = resolved 
      ? "Problema resolvido! Obrigado pelo atendimento."
      : prompt("Por favor, descreva o que ainda não está funcionando:");

    if (!resolved && !message) return;

    try {
      setIsSubmitting(true);
      
      await handleInteraction({
        type: 'validation',
        message: message || '',
        metadata: { resolved }
      });

    } catch (err) {
      console.error("Erro na validação:", err);
    }
  };

  const handleDownloadAttachment = async (attachmentId: number) => {
    try {
      const response = await authenticatedFetch(`/api/tickets/anexos.php?id=${attachmentId}`);
      
      if (!response.ok) {
        throw new Error("Erro ao baixar arquivo");
      }

      const data = await response.json();
      
      if (data.success && data.download_url) {
        window.open(data.download_url, '_blank');
      }
    } catch (err) {
      console.error("Erro ao baixar anexo:", err);
      setMessage({ type: "error", text: "Erro ao baixar arquivo" });
    }
  };

  const needsUserAction = (): boolean => {
    if (!ticket || !currentUser) return false;
    
    const userType = currentUser.user_type;
    const status = ticket.status;
    
    // Cliente precisa agir
    if (userType === 'client' && ticket.client_id === currentUser.client_id) {
      return status === 'aguardando_resposta';
    }
    
    // Atendente precisa agir
    if (userType === 'internal' && ticket.assigned_to === currentUser.id) {
      return ['em_atendimento', 'aguardando_resposta'].includes(status);
    }
    
    // Admin precisa agir (tickets novos)
    if (userType === 'internal' && currentUser.role === 'admin') {
      return status === 'novo' && !ticket.assigned_to;
    }
    
    return false;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <Button variant="ghost" asChild className="mb-4">
          <Link to="/cliente/tickets">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar para Tickets
          </Link>
        </Button>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="max-w-4xl mx-auto">
        <Button variant="ghost" asChild className="mb-4">
          <Link to="/cliente/tickets">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar para Tickets
          </Link>
        </Button>
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Ticket não encontrado</AlertDescription>
        </Alert>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.novo;
  const priorityConfig = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.media;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Message */}
      {message && (
        <Alert className={message.type === "success" ? "border-green-500 bg-green-50 dark:bg-green-950" : "border-red-500 bg-red-50 dark:bg-red-950"}>
          {message.type === "success" ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <AlertCircle className="h-4 w-4 text-red-600" />}
          <AlertDescription className={message.type === "success" ? "text-green-800 dark:text-green-200" : "text-red-800 dark:text-red-200"}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      {/* Header */}
      <div>
        <Button variant="ghost" asChild className="mb-4">
          <Link to="/cliente/tickets">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar para Tickets
          </Link>
        </Button>
        
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <span className="font-mono text-sm font-medium text-muted-foreground">
                #{ticket.ticket_number}
              </span>
              <Badge className={cn("text-xs", statusConfig.color)}>
                {statusConfig.label}
              </Badge>
              <Badge variant="outline" className={cn("text-xs border", priorityConfig.color)}>
                {priorityConfig.label}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {ticket.category}
              </Badge>
            </div>
            <h1 className="text-3xl font-bold tracking-tight">{ticket.subject}</h1>
            {ticket.assigned_to_name && (
              <p className="text-sm text-muted-foreground mt-2">
                Atribuído para: <strong>{ticket.assigned_to_name}</strong>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Action Required Alert */}
      {needsUserAction() && (
        <Alert className="border-orange-500 bg-orange-50 dark:bg-orange-950">
          <Bell className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-orange-800 dark:text-orange-200">
            <strong>Ação necessária:</strong> Este ticket está aguardando uma ação sua.
            {currentUser?.user_type === 'client' && ticket.status === 'aguardando_resposta' && 
              " Por favor, responda às informações solicitadas ou valide a solução."}
            {currentUser?.user_type === 'internal' && 
              " Por favor, dê andamento ao atendimento."}
          </AlertDescription>
        </Alert>
      )}

      {/* Validation Buttons for Client */}
      {currentUser?.user_type === 'client' && ticket.status === 'aguardando_resposta' && (
        <Card className="bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-green-900 dark:text-green-100">
                    Solução Aplicada
                  </h3>
                  <p className="text-sm text-green-800 dark:text-green-200 mt-1">
                    Nossa equipe implementou uma solução. Por favor, teste e nos informe se funcionou.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => handleValidation(true)}
                  disabled={isSubmitting}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Sim, funcionou!
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => handleValidation(false)}
                  disabled={isSubmitting}
                  className="flex-1 border-orange-500 text-orange-700 hover:bg-orange-50 dark:hover:bg-orange-950"
                >
                  <AlertCircle className="mr-2 h-4 w-4" />
                  Não, ainda há problemas
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ticket Information */}
      <Card>
        <CardHeader>
          <CardTitle>Informações do Ticket</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 text-sm">
            <div>
              <span className="text-muted-foreground">Cliente:</span>
              <span className="ml-2 font-medium">{ticket.company_name}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Criado em:</span>
              <span className="ml-2 font-medium">{formatDate(ticket.created_at)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Atualizado em:</span>
              <span className="ml-2 font-medium">{formatDate(ticket.updated_at)}</span>
            </div>
          </div>

          <div className="pt-4 border-t">
            <h3 className="font-semibold mb-2">Descrição</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {ticket.description}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Histórico do Ticket</CardTitle>
        </CardHeader>
        <CardContent>
          <TicketTimeline
            interactions={timeline}
            currentUserType={currentUser?.user_type || 'client'}
            onDownloadAttachment={handleDownloadAttachment}
          />
        </CardContent>
      </Card>

      {/* Interaction Form */}
      <TicketInteractionForm
        ticketId={id!}
        ticketStatus={ticket.status}
        userType={currentUser?.user_type || 'client'}
        onSubmit={handleInteraction}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default ClientTicketDetailNew;
